import React from "react";
import classnames from 'classnames';
import { Translate } from 'react-localize-redux';
import Paging from './pagingComponent';
import BbCollapse from "./BbCollapse";

/**
 * @param {bool} offsetLeft - true adds 2.5 padding on left via old-style-left-offset css class
 * @param {bool} offsetLeftHalf - true adds 1.25 padding on left via old-style-left-offset-half css class
 * @param {function} head - function for thead rending
 * @param {function} historyHead - function for history thead rending
 * @param {number} tableColumns - used as colSpan for paging row
 * @param {Array} colWidths - array of column width items used to create <colgroup> element - if this is populated tableColumns is not needed
 * @param {bool} colWidthsOnMobile - use column widths on mobile
 * @param {Array} actualData - actual data array
 * @param {Array} historyData - history data array
 * @param {function} actualRow - function for rendering actual data row
 * @param {function} historyRow - function for rendering history data row
 * @param {string} showAllLink - redirect url for all content
 * @param {function} resolveExpanded - function to determine if subsections should be expanded (passed from reportActions)
 * @param {bool} stripped - apply old-style-stripped class to <tbody>
 * @param {bool} fixed - use fixed table layout
 * @param {object} classNames - dictionary of classes with conditions
 */
class OldStyleTableWithHistory extends React.Component {
    static defaultProps = {
        colWidthsOnMobile: true,
        fixed: true,
        classNames: {}
    };

    state = {
        historyExpanded: false,
        isHistoryExpandedTouched: false,
        page: {
            actual: 1,
            historical: 1
        }
    };


    render() {
        const hasActual = (this.props.actualData || []).length > 0;
        const hasHistory = (this.props.historyData || []).length > 0;

        let cls = { 'old-style-left-offset': this.props.offsetLeft === true, 'old-style-left-offset-half': this.props.offsetLeftHalf === true, 'fixed': this.props.fixed !== false };
        if (typeof this.props.classNames === 'string' || this.props.classNames instanceof String) {
            cls[this.props.classNames] = true;
        } else {
            cls = { ...cls, ...this.props.classNames }
        }

        const tableMargin = this.props.noMargin === true ? undefined : { marginBottom: '1.5em' };

        return (
            <table className={classnames("table old-style-table-report", cls)} style={tableMargin}>
                {this.props.colWidths && <colgroup className={classnames({ "hide-on-mobile": !this.props.colWidthsOnMobile })}>
                    {this.props.colWidths.map((cw, i) => <col key={i} width={cw} />)}
                </colgroup>}
                {this.props.head && this.props.head()}
                {(hasActual || hasHistory) && this.renderActualTable(this.props.actualData, hasActual, hasHistory)}
                {hasHistory && this.renderHistoryTable(this.props.historyData, hasActual)}
            </table>
        );
    }

    renderActualTable = (data, hasActual, hasHistorical) => {
        let dataSlice = [];
        let hasExtraRow = hasHistorical;
        let showPagingOrAll = false
        let renderMoreButton = this.props.showAsMore === true;
        if (this.props.pageSize === null || this.props.pageSize === undefined) {
            dataSlice = data;
        } else {
            const min = this.props.isLimited ? 0 : (this.state.page.actual - 1) * this.props.pageSize;
            const max = this.getMaxItemIndex(data.length, this.props.pageSize, this.props.isLimited, false);
            hasExtraRow = (data.length > this.props.pageSize) || hasHistorical;
            dataSlice = data.slice(min, max);
            showPagingOrAll = data.length > this.props.pageSize;
        }

        return (
            <tbody className={classnames({ 'old-style-stripped': this.props.stripped === true })}>
                {hasActual && this.props.actualRow && dataSlice.map((r, i) => this.props.actualRow(r, i))}
                {(hasExtraRow && !renderMoreButton) && <tr className='without-hover without-strip'>
                    <td colSpan={((this.props.colWidths || {}).length) || this.props.tableColumns}>
                        <div style={{ display: 'flex', flex: '1 1 auto' }}>
                            <div style={{ display: 'flex', flex: '1 1 0%' }}>
                                {showPagingOrAll && this.props.isLimited && this.props.showAllLink && this.renderShowAll(data.length)}
                            </div>
                            <div style={{ display: 'flex', flex: '1 1 0%', justifyContent: 'center' }}>
                                {showPagingOrAll && !this.props.isLimited && this.renderPaging(data, 'actual')}
                            </div>
                            <div style={{ display: 'flex', flex: '1 1 0%', justifyContent: 'flex-end' }}>
                                {(hasHistorical && !renderMoreButton) && this.renderHistoryOpener()}
                            </div>
                        </div>
                    </td>
                </tr>}
                {(hasExtraRow && renderMoreButton) && <tr className='without-hover without-strip'>
                    <td colSpan={((this.props.colWidths || {}).length) || this.props.tableColumns}>
                        {(hasHistorical && renderMoreButton) && this.renderMoreOpener()}
                    </td>
                </tr>}
            </tbody>
        );
    }

    renderHistoryTable = (data, hasActual) => {
        let dataSlice = [];
        let showPagingOrAll = false
        if (this.props.pageSize === null || this.props.pageSize === undefined) {
            dataSlice = data;
        } else {
            const min = this.props.isLimited ? 0 : (this.state.page.historical - 1) * this.props.pageSize;
            const max = this.getMaxItemIndex(data.length, this.props.pageSize, this.props.isLimited, true);
            dataSlice = data.slice(min, max);
            showPagingOrAll = data.length > this.props.pageSize;
        }

        return (
            <BbCollapse tag='tbody' isOpen={this.props.resolveExpanded(this.state.historyExpanded, this.state.isHistoryExpandedTouched)}>
                {this.props.historyHead && this.props.historyHead(true)}
                {this.props.historyRow && dataSlice.map((r, i) => this.props.historyRow(r, i))}
                {showPagingOrAll && <tr className='without-hover without-strip'>
                    <td colSpan={this.props.tableColumns}>
                        {this.props.isLimited && this.props.showAllLink && this.renderShowAll(data.length)}
                        {!this.props.isLimited && this.renderPaging(data, 'historical')}
                    </td>
                </tr>}
            </BbCollapse>
        );
    }

    renderShowAll = (totalCount) => {
        // sanitize showAllLink prop
        const allLink = /^unlimited\/[a-zA-Z]+\/\d+(\/[a-zA-Z]+)?$/.test(this.props.showAllLink) ? this.props.showAllLink : "#";
        return (
            <div>
                <a className="link-functional" target="_blank" href={allLink}>
                    <Translate id="Report.Common.ShowAll" />&nbsp;({totalCount})
                </a>
            </div>
        );
    }

    renderPaging = (data, tableType) => {
        return (
            <div>
                <Paging
                    justify="center"
                    selectedPage={this.state.page[tableType]}
                    totalPages={Math.ceil(data.length / this.props.pageSize)}
                    setPage={page => this.setState({ page: { ...this.state.page, [tableType]: page } })}
                />
            </div>
        );
    }

    renderHistoryOpener = () => {
        return (
            <span style={{ cursor: 'pointer', float: 'right' }} onClick={this.toggleHistory} className='link-functional link-orange'>
                {this.props.resolveExpanded(this.state.historyExpanded, this.state.isHistoryExpandedTouched) === true ? <Translate id="Report.Common.HideHistory" /> : <Translate id="Report.Common.History" />}
            </span>
        )
    }

    renderMoreOpener = () => {
        return (
            <span style={{ cursor: 'pointer', float: 'left' }} onClick={this.toggleHistory} className='link-functional'>
                {this.props.resolveExpanded(this.state.historyExpanded, this.state.isHistoryExpandedTouched) === true ? <Translate id="Report.Common.Less" /> : <Translate id="Report.Common.More" />}
            </span>
        )
    }

    toggleHistory = () => {
        const newExpanded = !this.props.resolveExpanded(this.state.historyExpanded, this.state.isHistoryExpandedTouched);
        this.setState({ historyExpanded: newExpanded, isHistoryExpandedTouched: true });
        this.props.onHistoryToggle && this.props.onHistoryToggle(newExpanded);
    }

    getMaxItemIndex = (count, pageSize, isLimited, isHistory) => {
        if (isLimited)
            return count < pageSize ? count : pageSize;
        return pageSize * this.state.page[isHistory ? 'historical' : 'actual'] > count
            ? count : pageSize * this.state.page[isHistory ? 'historical' : 'actual'];
    }
}

export default OldStyleTableWithHistory